// 自动整定引擎（增强版）
// 根据当前参数与目标策略，推荐曲线族与时间倍数 TMS，
// 并为距离/差动提供可应用的建议（线路角、线路参数、差动分段等）。

import { applyFeederPresetToDistance, computeTotalsFromPerKm } from './distanceLinkage';
import { getFeederById } from './deviceLibrary';

// IEC 60255 曲线常数（与 tccUtils/protectionCalculations 保持一致）
const CURVE_CONSTS = {
  'Normal Inverse': { k: 0.14, alpha: 0.02 }, // NI
  'Very Inverse': { k: 13.5, alpha: 1 },      // VI
  'Extremely Inverse': { k: 80, alpha: 2 },   // EI
  'Long-time Inverse': { k: 120, alpha: 1 }   // LTI（用于长延时场景）
};

const normalizeCurveType = (curve) => {
  if (!curve) return 'Normal Inverse';
  const v = String(curve).trim();
  const map = {
    NI: 'Normal Inverse',
    VI: 'Very Inverse',
    EI: 'Extremely Inverse',
    LTI: 'Long-time Inverse'
  };
  return CURVE_CONSTS[v] ? v : (map[v] || v);
};

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

// 目标策略到期望动作时间区间（秒）的简单映射
const GOAL_TARGET_TIME = {
  fast: 0.35,
  selective: 0.5,
  sensitive: 0.6
};

// 过电流与方向保护：推荐曲线与 TMS
export const recommendForOvercurrentLike = (params, options = {}) => {
  const {
    ratedCurrent = 0,
    ctRatio = 1,
    faultCurrent = 0,
    curveType: curveInput
  } = params || {};

  const goal = options.goal || 'selective';
  const targetTime = GOAL_TARGET_TIME[goal] || GOAL_TARGET_TIME.selective; // s

  const pickup = (ratedCurrent * 1.3) / (ctRatio || 1); // Iop（二次）
  const IsecFault = (faultCurrent || 0) / (ctRatio || 1); // 故障二次电流
  const M = pickup > 0 ? (IsecFault / pickup) : 0;

  // 简单规则选择曲线族
  let curveType = normalizeCurveType(curveInput);
  if (!curveType || !CURVE_CONSTS[curveType]) {
    if (M > 8) curveType = 'Extremely Inverse';
    else if (M > 4) curveType = 'Very Inverse';
    else curveType = 'Normal Inverse';
  }

  const { k, alpha } = CURVE_CONSTS[curveType] || CURVE_CONSTS['Normal Inverse'];
  // 反推 TMS：t = (k * TMS) / (M^alpha - 1)
  const denom = Math.pow(M || 1, alpha) - 1;
  let TMS = denom > 0 ? (targetTime * denom) / k : 0.5;
  TMS = clamp(+(+TMS).toFixed(3), 0.05, 1.0);

  const rationale = [
    `故障二次电流 Isec=${IsecFault.toFixed(2)}A，启动电流 Iop≈${pickup.toFixed(3)}A，倍数 M≈${M.toFixed(2)}`,
    `目标策略=${goal}，目标动作时间≈${targetTime}s，曲线族=${curveType}`,
    `根据 IEC 反时限关系 t=(k·TMS)/(M^α-1)，反算推荐 TMS≈${TMS}`
  ];

  const warnings = [];
  if (M <= 1) warnings.push('当前故障倍数 M≤1，反时限不动作（理论值为∞），请检查 CT 比或负荷/故障电流输入');

  return {
    suggestions: { curveType, timeMultiplier: TMS },
    appliedFields: ['curveType', 'timeMultiplier'],
    rationale,
    warnings
  };
};

// 距离保护：推荐线路角与（若可用）馈线线路参数预填
export const recommendForDistance = (params, options = {}) => {
  const suggestions = {};
  const appliedFields = [];
  const rationale = [];
  const warnings = [];

  // 线路角推导：优先显式角 → R/X → 馈线 → 默认75°
  let lineAngleDeg = typeof params?.lineAngleDeg === 'number' ? params.lineAngleDeg : undefined;
  if (!(typeof lineAngleDeg === 'number' && isFinite(lineAngleDeg))) {
    const r = params?.lineR_ohm; const x = params?.lineX_ohm;
    if (r != null && x != null) {
      const ang = Math.atan2(Number(x) || 0, Number(r) || 0) * 180 / Math.PI;
      if (isFinite(ang)) lineAngleDeg = parseFloat(Number(ang).toFixed(1));
      rationale.push('线路角来源：根据当前 R/X 推算');
    }
  }
  if (!(typeof lineAngleDeg === 'number' && isFinite(lineAngleDeg)) && params?.selectedFeederId) {
    try {
      const f = getFeederById(params.selectedFeederId);
      const Rpkm = f?.line?.R_per_km_ohm ?? f?.R_per_km_ohm ?? 0;
      const Xpkm = f?.line?.X_per_km_ohm ?? f?.X_per_km_ohm ?? 0;
      const Lkm = f?.line?.length_km ?? f?.length_km ?? 0;
      const R = (Number(Rpkm) || 0) * (Number(Lkm) || 0);
      const X = (Number(Xpkm) || 0) * (Number(Lkm) || 0);
      const ang = Math.atan2(X, R) * 180 / Math.PI;
      if (isFinite(ang)) lineAngleDeg = parseFloat(Number(ang).toFixed(1));
      rationale.push('线路角来源：设备库馈线 R/X 推算');
    } catch (_) {}
  }
  if (!(typeof lineAngleDeg === 'number' && isFinite(lineAngleDeg))) {
    lineAngleDeg = 75;
    rationale.push('线路角来源：默认 75° 回退');
  }
  suggestions.lineAngleDeg = lineAngleDeg;
  appliedFields.push('lineAngleDeg');

  // 若存在 selectedFeederId，则建议从馈线预填线路参数（每公里与总阻抗）
  if (params?.selectedFeederId) {
    try {
      const f = getFeederById(params.selectedFeederId);
      const patch = applyFeederPresetToDistance(f);
      Object.assign(suggestions, patch);
      appliedFields.push('lineImpedanceUnitMode','lineLength_km','lineR_per_km_ohm','lineX_per_km_ohm','lineR_ohm','lineX_ohm');
      rationale.push('线路参数来源：应用设备库馈线的长度与每公里R/X');
    } catch (e) {
      warnings.push('无法从设备库读取馈线参数，已跳过线路参数预填');
    }
  } else {
    // 若用户选择了 per_km 单位且提供长度与每公里参数，建议计算总阻抗
    const mode = params?.lineImpedanceUnitMode;
    const totals = computeTotalsFromPerKm({
      mode,
      lineLength_km: params?.lineLength_km,
      lineR_per_km_ohm: params?.lineR_per_km_ohm,
      lineX_per_km_ohm: params?.lineX_per_km_ohm
    });
    if (Object.keys(totals).length) {
      Object.assign(suggestions, totals);
      appliedFields.push('lineR_ohm','lineX_ohm');
      rationale.push('总阻抗来源：根据 per_km 参数与长度计算');
    }
  }

  return { suggestions, appliedFields, rationale, warnings };
};

// 差动保护：推荐特性分段（若未提供 segments）
export const recommendForDifferential = (params, options = {}) => {
  const ratedCurrent = Number(params?.ratedCurrent) || 0;
  const ctRatio = Number(params?.ctRatio) || 1;

  // 参考值：差动定值 0.4A（二次），斜率 0.5；速断 6×Irated（一次）
  const diffPickup = 0.4;
  const slope = 0.5;
  const instantaneousSecondary = (ratedCurrent * 6) / (ctRatio || 1);

  const hasUserSegs = Array.isArray(params?.segments) && params.segments.some(sg => typeof sg?.slope === 'number');
  const segments = hasUserSegs
    ? params.segments.map(seg => ({ slope: Number(seg.slope), threshold: Number(seg.threshold), label: seg.label }))
    : [
        { slope: 0, threshold: parseFloat(diffPickup.toFixed(2)), label: 'K=0' },
        { slope: parseFloat(slope.toFixed(2)), threshold: Infinity, label: `K=${parseFloat(slope.toFixed(2))}` }
      ];

  const suggestions = { segments };
  const appliedFields = ['segments'];
  const rationale = [
    `差动定值参考：Id_pickup=${diffPickup}A（二次），斜率 K=${slope}`,
    `速断参考：6×Irated，一次=${(ratedCurrent * 6).toFixed(0)}A，二次≈${instantaneousSecondary.toFixed(3)}A`
  ];
  const warnings = [];
  if (!ratedCurrent) warnings.push('未提供额定电流，速断二次值仅供参考');
  if (!ctRatio) warnings.push('未提供CT变比，速断二次值仅供参考');

  return { suggestions, appliedFields, rationale, warnings };
};

// 统一入口：按保护类型选择推荐方法
export const recommendSettings = (values, options = {}) => {
  const type = values?.protectionType || 'overcurrent';
  if (type === 'overcurrent' || type === 'directional' || type === 'zero_sequence') {
    return recommendForOvercurrentLike(values, options);
  }
  if (type === 'distance') {
    return recommendForDistance(values, options);
  }
  if (type === 'differential') {
    return recommendForDifferential(values, options);
  }
  return {
    suggestions: {},
    appliedFields: [],
    rationale: ['当前保护类型的自动整定尚未实现，敬请期待'],
    warnings: []
  };
};

export default { recommendSettings, recommendForOvercurrentLike, recommendForDistance, recommendForDifferential };